import React, { useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  InputBase,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSearchTokens } from "../../../SearchScreen/searchDesignTokens";
import FlagEmoji from "../../../../components/common/FlagEmoji";
import ContractCountryJurisdictionPicker from "../../../../components/common/ContractCountryJurisdictionPicker";

const LANGUAGE_OPTIONS = [
  { value: "fr",  label: "Français", flag: "🇫🇷" },
  { value: "en",  label: "English",  flag: "🇬🇧" },
  { value: "ar",  label: "العربية",   flag: "🇸🇦" },
  { value: "de",  label: "Deutsch",  flag: "🇩🇪" },
  { value: "it",  label: "Italiano", flag: "🇮🇹" },
];

const TemplateOption = ({
  selectedCountry,
  setSelectedCountry,
  onCountryChange,
  selectedLanguage,
  setSelectedLanguage,
  contractTypeName,
  setContractTypeName,
  selectedJurisdictionCodes,
  setSelectedJurisdictionCodes,
  mobile,
}) => {
  const { t } = useTranslation();
  const dk = useSearchTokens();
  const [promptFocused, setPromptFocused] = useState(false);

  // Shared field-label style — pairs the jurisdiction picker and the language
  // select with the same "label above input" pattern.
  const fieldLabelSx = {
    fontSize: '0.72rem',
    fontWeight: 500,
    color: dk.text3,
    ml: 0.25,
  };

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.82rem',
      backgroundColor: dk.inputBg,
      borderRadius: '8px',
      height: 40,
      '& fieldset': { borderColor: dk.border },
      '&:hover fieldset': { borderColor: dk.borderHover },
      '&.Mui-focused fieldset': { borderColor: dk.goldBdr, borderWidth: 1 },
    },
    '& .MuiSelect-select': { color: dk.text1, py: 0, display: 'flex', alignItems: 'center' },
    '& .MuiSvgIcon-root': { color: dk.text3 },
  };

  const handleCountryChange = (cc) => {
    setSelectedCountry(cc);
    if (onCountryChange) onCountryChange(cc);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", gap: 2.5 }}>

      {/* Header */}
      <Box>
        <Typography
          sx={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: '1.1rem',
            fontWeight: 500,
            color: dk.text1,
            mb: 0.5,
          }}
        >
          {t("generate_contract") || "Generate Contract"}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: dk.text3, lineHeight: 1.6 }}>
          {t("generate_contract_subtitle") ||
            "Select your jurisdiction and language, then describe the contract — we'll draft it for you."}
        </Typography>
      </Box>

      {/* Country+jurisdiction picker (single dropdown) + Language */}
      <Box sx={{ display: "flex", gap: 1.5, flexDirection: mobile ? "column" : "row", alignItems: "stretch" }}>
        <Box data-tour="contract-jurisdiction-picker" sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography sx={fieldLabelSx}>{t("jurisdiction") || "Jurisdiction"}</Typography>
          <ContractCountryJurisdictionPicker
            country={selectedCountry}
            onCountryChange={handleCountryChange}
            jurisdictionCodes={selectedJurisdictionCodes}
            onJurisdictionCodesChange={setSelectedJurisdictionCodes || (() => {})}
          />
        </Box>

        <Box data-tour="contract-language-select" sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography sx={fieldLabelSx}>{t("language") || "Language"}</Typography>
          <FormControl size="small" sx={selectSx}>
            <Select
              value={selectedLanguage || "en"}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              startAdornment={
                <InputAdornment position="start" sx={{ ml: -0.25 }}>
                  <span style={{ fontSize: '1rem' }}>🌐</span>
                </InputAdornment>
              }
              renderValue={(val) => {
                const opt = LANGUAGE_OPTIONS.find((l) => l.value === val);
                return opt ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <FlagEmoji emoji={opt.flag} size={18} />
                    <span>{opt.label}</span>
                  </Box>
                ) : val;
              }}
            >
              {LANGUAGE_OPTIONS.map((lang) => (
                <MenuItem key={lang.value} value={lang.value}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <FlagEmoji emoji={lang.flag} size={18} />
                    <span>{lang.label}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Prompt area */}
      <Box data-tour="contract-prompt-area" sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            color: dk.text3,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            mb: 1,
          }}
        >
          {t("describe_contract") || "Describe the contract you need"}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            flex: 1,
            backgroundColor: dk.inputBg,
            border: `1px solid ${promptFocused ? dk.goldBdr : dk.border}`,
            borderRadius: "8px",
            padding: "10px 12px",
            boxShadow: promptFocused ? `0 0 0 1px ${dk.goldBdr}` : 'none',
            transition: "border-color 0.18s, box-shadow 0.18s",
            minHeight: mobile ? 140 : 200,
          }}
        >
          <InputBase
            fullWidth
            multiline
            minRows={mobile ? 5 : 8}
            placeholder={
              t("custom_type_placeholder") ||
              "e.g. Consultancy Agreement (2 pages max), employment contract for a software developer with a non-compete clause…"
            }
            value={contractTypeName}
            onChange={(e) => setContractTypeName(e.target.value)}
            onFocus={() => setPromptFocused(true)}
            onBlur={() => setPromptFocused(false)}
            sx={{
              flex: 1,
              fontSize: "0.82rem",
              lineHeight: 1.6,
              color: dk.text1,
              "& .MuiInputBase-input": {
                padding: 0,
                color: dk.text1,
              },
              "& .MuiInputBase-input::placeholder": {
                color: dk.text3,
                opacity: 1,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TemplateOption;
